import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Cloud as CloudIcon,
  CloudQueue as OneDriveIcon,
  CloudCircle as DropboxIcon,
  CloudOutlined as NextcloudIcon,
  Sync as SyncIcon,
  PlayArrow as TestIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  Box,
  Avatar,
  Tooltip,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../../utils/formatting/formatters';
import { ExtendedConnectionResponse } from '../../../types/common';

interface ConnectionCardProps {
  connection: ExtendedConnectionResponse;
  onEdit: (connection: ExtendedConnectionResponse) => void;
  onDelete: (connection: ExtendedConnectionResponse) => void;
  onSync: (connectionId: string) => void;
  onTest: (connectionId: string) => void;
  onReconnect?: (connectionId: string) => void;
}

const getProviderIcon = (providerType: string) => {
  switch (providerType.toLowerCase()) {
    case 'onedrive':
      return <OneDriveIcon />;
    case 'dropbox':
      return <DropboxIcon />;
    case 'nextcloud':
      return <NextcloudIcon />;
    default:
      return <CloudIcon />;
  }
};

const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'default';
    case 'error':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

export const ConnectionCard: React.FC<ConnectionCardProps> = ({
  connection,
  onEdit,
  onDelete,
  onSync,
  onTest,
  onReconnect
}) => {
  const { t, i18n } = useTranslation();
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
            {getProviderIcon(connection.provider_type)}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6" component="div">
              {connection.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {connection.provider_name}
            </Typography>
          </Box>
          <Box>
            <Chip
              label={t(`documentSources.status.${connection.status}`)}
              color={getStatusColor(connection.status)}
              size="small"
              variant={connection.enabled ? "filled" : "outlined"}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>{t('documentSources.info.folder')}:</strong> {connection.folder_path || '/'}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>{t('documentSources.info.sync')}:</strong> {t(`documentSources.syncInterval.${connection.sync_interval}`)}
          </Typography>
          {connection.last_sync_at && (
            <Typography variant="body2" color="text.secondary">
              <strong>{t('documentSources.info.lastSync')}:</strong> {formatDateTime(connection.last_sync_at, i18n.language)}
            </Typography>
          )}
        </Box>

        {connection.share_url && (
          <Box display="flex" alignItems="center" gap={1}>
            <LinkIcon fontSize="small" color="action" />
            <Typography variant="body2" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {connection.share_url}
            </Typography>
          </Box>
        )}

        {connection.needs_reconnect && (
          <Box mt={2}>
            <Chip
              label={t('documentSources.status.reconnectionRequired')}
              color="warning"
              size="small"
              onClick={() => onReconnect?.(connection.id)}
            />
          </Box>
        )}
      </CardContent>
      <CardActions>
        <Tooltip title={t('documentSources.actions.testConnection')}>
          <IconButton size="small" onClick={() => onTest(connection.id)}>
            <TestIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('documentSources.actions.syncNow')}>
          <IconButton size="small" onClick={() => onSync(connection.id)}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('documentSources.actions.edit')}>
          <IconButton size="small" onClick={() => onEdit(connection)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('documentSources.actions.delete')}>
          <IconButton size="small" onClick={() => onDelete(connection)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};